import { observer } from 'mobx-react-lite';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { cn } from '@/lib/utils';

import { agentRunsStore } from './agent-runs.store';
import {
  describeOutcome,
  formatDuration,
  outcomeOf,
  parseExecResult,
  parseTerminalRunArgs,
  preDispatchError,
  rawTerminalRunArgs,
} from './terminal-run-card.utils';
import { useAgentRunTail } from './use-agent-run-tail';

import type { ApiToolCallModel, Modality } from '@littlebird/sdk';

const MONO = 'font-mono text-xs leading-relaxed';

/**
 * The chat's view of one agent-run shell command (`TERMINAL_RUN`). Runs are
 * headless - no terminal tab opens on its own - and the card stays quiet by
 * default: the command line plus a status (spinner, then exit code and
 * duration). "Show output" reveals what the command printed: a live tail while
 * it runs, the final output once it ends. That output is the same thing a
 * terminal tab would show, so there is no separate attach affordance.
 */
export const TerminalRunCard = observer(function TerminalRunCard({
  modality,
  toolCall,
  stepId,
}: {
  modality: Modality;
  toolCall?: ApiToolCallModel;
  stepId?: string;
}) {
  const [showOutput, setShowOutput] = useState(false);
  const [showRejection, setShowRejection] = useState(false);
  const [commandExpanded, setCommandExpanded] = useState(false);
  const commandRef = useRef<HTMLPreElement>(null);
  const [commandOverflows, setCommandOverflows] = useState(false);
  const args = parseTerminalRunArgs(modality, toolCall);
  const storedResult = parseExecResult(toolCall?.result);

  // Which run is ours, most to least certain: the tool-call id the host echoed
  // back; the one bound to this step earlier;
  // the session id in a stored tool result; one bound to a sibling step of the
  // same message (indices can shift on finalize); else the latest unclaimed run
  // of this command line. Bind on first sight so later renders are exact.
  const run =
    (toolCall?.tool_id
      ? agentRunsStore.byToolCallId(toolCall.tool_id)
      : undefined) ??
    (stepId ? agentRunsStore.byStep(stepId) : undefined) ??
    (storedResult
      ? agentRunsStore.bySession(storedResult.sessionId)
      : undefined) ??
    (stepId && args
      ? agentRunsStore.bySiblingStep(stepId, args.command)
      : undefined) ??
    (args ? agentRunsStore.latestUnboundByCommand(args.command) : undefined);
  useEffect(() => {
    if (stepId && run && run.stepId === undefined) {
      agentRunsStore.bindStep(run.sessionId, stepId);
    }
  }, [stepId, run]);
  const result = run?.result ?? storedResult;
  // Spin only on positive evidence of a live command: a pending tool call, or a
  // run the host reported started but not finished. A card with neither (e.g. a
  // run from before the store persisted, seen after a reload) shows no status.
  const running = run !== undefined && run.result === null;
  const inFlight = !result && (toolCall?.status === 'pending' || running);
  // The toolkit answered without running anything (device offline, unknown
  // device, toolkit disabled): the tool call holds the error text instead of
  // an exec result. BE-3143 is what delivers that call to the client.
  const rejection = !result && !running ? preDispatchError(toolCall) : null;
  // Only stream while someone is looking; the final output arrives regardless.
  const tail = useAgentRunTail(run?.sessionId ?? null, running && showOutput);

  // Does the clamp actually hide part of the command?
  useLayoutEffect(() => {
    const el = commandRef.current;
    if (!el || commandExpanded) return;
    setCommandOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [args?.command, commandExpanded]);

  if (!args) {
    return (
      <UnparseableTerminalRunCard
        raw={rawTerminalRunArgs(modality, toolCall)}
        error={toolCall?.result ?? null}
      />
    );
  }
  const commandClamped = commandOverflows && !commandExpanded;
  const toggleCommand = () => setCommandExpanded((v) => !v);

  const outcome = result ? outcomeOf(result) : null;
  const finalOutput = result?.output.trimEnd() ?? '';
  const output = result ? finalOutput : tail;
  const hasOutput = result ? finalOutput.length > 0 : running;

  return (
    <div className="flex flex-col gap-1">
      {args.why && (
        <span className="text-sm text-foreground-muted">{args.why}</span>
      )}
      <div className="rounded-md border border-border-strongest/60 bg-surface-hover/40">
        <div className="flex items-start gap-2 px-2.5 py-1.5">
          <span className={cn(MONO, 'select-none text-foreground-subtle')}>
            $
          </span>
          <pre
            ref={commandRef}
            className={cn(
              MONO,
              'min-w-0 flex-1 whitespace-pre-wrap break-all',
              // Clamped: two lines. Expanded: the same cap as the output region
              // below, scrolling past it, so a pasted script can't take over the
              // thread. Literal class names so Tailwind sees them.
              commandExpanded ? 'max-h-80 overflow-y-auto' : 'line-clamp-2',
              // Fade the last line so a clamped command is visibly cut off.
              commandClamped &&
                'cursor-pointer [mask-image:linear-gradient(to_bottom,black_50%,transparent)]'
            )}
            title={commandClamped ? 'Show full command' : undefined}
            onClick={commandClamped ? toggleCommand : undefined}
          >
            {args.command}
          </pre>
          <div className="flex shrink-0 items-center gap-2 text-xs text-foreground-muted">
            {(outcome || inFlight || rejection) && (
              <span
                className={cn(
                  'flex items-center gap-1',
                  outcome
                    ? outcome.kind === 'ok'
                      ? 'text-success-9'
                      : 'text-danger-10'
                    : rejection
                      ? 'text-danger-10'
                      : 'text-foreground-muted'
                )}
              >
                {outcome ? (
                  <>
                    {outcome.kind === 'ok' ? (
                      <Icons.check className="size-3" />
                    ) : (
                      <Icons.cancel className="size-3" />
                    )}
                    {describeOutcome(outcome)}
                    {result && (
                      <span className="text-foreground-muted">
                        · {formatDuration(result.durationMs)}
                      </span>
                    )}
                  </>
                ) : rejection ? (
                  <>
                    <Icons.cancel className="size-3" />
                    didn't run
                  </>
                ) : (
                  <Icons.loader className="size-3 animate-spin" />
                )}
              </span>
            )}
            {rejection && (
              <DetailsButton
                open={showRejection}
                onToggle={() => setShowRejection((v) => !v)}
              />
            )}
            {/* Unfolds the clamped command; the strip below is for the output. */}
            {commandOverflows && (
              <button
                type="button"
                aria-expanded={commandExpanded}
                aria-label={
                  commandExpanded ? 'Collapse command' : 'Show full command'
                }
                className="-my-0.5 flex size-6 items-center justify-center rounded-md hover:bg-secondary-hover hover:text-foreground"
                onClick={toggleCommand}
              >
                {commandExpanded ? (
                  <Icons.chevronUp className="size-3" />
                ) : (
                  <Icons.chevronDown className="size-3" />
                )}
              </button>
            )}
          </div>
        </div>
        {rejection && showRejection && <DetailsPane text={rejection} />}

        {/* Output drawer: a bare centered chevron. Expanded, it stays on top
            as the way back.
            Not the shared Button: its import graph reaches
            @/features/electron-api, which breaks chat tests that mock @/env. */}
        {hasOutput && (
          <div className="border-t border-border-strongest/40">
            <button
              type="button"
              aria-expanded={showOutput}
              aria-label={showOutput ? 'Hide output' : 'Show output'}
              className="flex w-full items-center justify-center py-1 text-foreground-subtle hover:bg-secondary-hover hover:text-foreground"
              onClick={() => setShowOutput((v) => !v)}
            >
              {showOutput ? (
                <Icons.chevronUp className="size-3" />
              ) : (
                <Icons.chevronDown className="size-3" />
              )}
            </button>
            {showOutput && (
              <pre
                className={cn(
                  MONO,
                  'max-h-80 overflow-y-auto px-2.5 pb-1.5 whitespace-pre-wrap break-all text-foreground-muted'
                )}
              >
                {output || 'Running...'}
                {result?.truncated && (
                  <span className="block pt-1 text-foreground-subtle">
                    (earlier output cut)
                  </span>
                )}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

/**
 * The agent called `TERMINAL_RUN` with arguments the card could not read as a
 * command (typically malformed JSON from the model). Nothing ran, so there is
 * no session to tail; show what was sent and what the toolkit answered, in the
 * same shell as a normal card so the step is not a dead end.
 */
function UnparseableTerminalRunCard({
  raw,
  error,
}: {
  raw: string | null;
  error: string | null;
}) {
  const [showError, setShowError] = useState(false);
  const detail = error?.trim() ?? '';
  return (
    <div className="rounded-md border border-border-strongest/60 bg-surface-hover/40">
      <div className="flex items-start gap-2 px-2.5 py-1.5">
        <span className={cn(MONO, 'select-none text-foreground-subtle')}>
          $
        </span>
        <pre
          className={cn(
            MONO,
            'line-clamp-2 min-w-0 flex-1 whitespace-pre-wrap break-all text-foreground-muted'
          )}
        >
          {raw ?? '(no arguments)'}
        </pre>
        <div className="flex shrink-0 items-center gap-2 text-xs text-foreground-muted">
          <span className="flex items-center gap-1 text-danger-10">
            <Icons.cancel className="size-3" />
            couldn't parse command
          </span>
          {detail && (
            <DetailsButton
              open={showError}
              onToggle={() => setShowError((v) => !v)}
            />
          )}
        </div>
      </div>
      {showError && detail && <DetailsPane text={detail} />}
    </div>
  );
}

/** Toggles the toolkit's error text for a call that never ran. */
function DetailsButton({
  open,
  onToggle,
}: {
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-expanded={open}
      className="-my-0.5 flex h-6 items-center rounded-md px-1.5 leading-none hover:bg-secondary-hover hover:text-foreground"
      onClick={onToggle}
    >
      {open ? 'Hide details' : 'Show details'}
    </button>
  );
}

function DetailsPane({ text }: { text: string }) {
  return (
    <pre
      className={cn(
        MONO,
        'max-h-80 overflow-y-auto border-t border-border-strongest/40 px-2.5 py-1.5 whitespace-pre-wrap break-all text-foreground-muted'
      )}
    >
      {text}
    </pre>
  );
}
